/**
 * Cached reference data (O-08, docs/09 §9.8).
 *
 * Served from IndexedDB immediately so the entry form works offline, then
 * refreshed from the server in the background. If the server's reference
 * version has moved on, the cache is replaced.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { reference } from "@/lib/api";
import { loadReference, saveReference } from "@/lib/db";
import type { Antibiotic, Organism, ReferenceBundle, Specimen, Ward } from "@/lib/types";

interface ReferenceValue {
  bundle: ReferenceBundle | null;
  loading: boolean;
  /** True when the copy on screen came from the cache, not the network. */
  fromCache: boolean;
  refresh: () => Promise<void>;
  organisms: Organism[];
  antibiotics: Antibiotic[];
  wards: Ward[];
  specimens: Specimen[];
  organismName: (code: string | null) => string;
  wardName: (code: string | null) => string;
  specimenName: (code: string | null) => string;
}

const ReferenceContext = createContext<ReferenceValue | null>(null);

const EMPTY: ReferenceBundle = {
  version: 0,
  organisms: [],
  antibiotics: [],
  wards: [],
  specimens: [],
  qa_rules: [],
};

export function ReferenceProvider({ children }: { children: ReactNode }) {
  const [bundle, setBundle] = useState<ReferenceBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [fromCache, setFromCache] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const fresh = await reference.bundle();
      setBundle(fresh);
      setFromCache(false);
      await saveReference(fresh);
    } catch {
      // Offline: whatever is cached stays on screen.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const cached = await loadReference();
      if (!cancelled && cached) {
        setBundle(cached);
        setFromCache(true);
        setLoading(false);
      }
      if (!cancelled) await refresh();
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value = useMemo<ReferenceValue>(() => {
    const data = bundle ?? EMPTY;
    const organismsByCode = new Map(data.organisms.map((o) => [o.code, o]));
    const wardsByCode = new Map(data.wards.map((w) => [w.code, w]));
    const specimensByCode = new Map(data.specimens.map((s) => [s.code, s]));

    return {
      bundle,
      loading,
      fromCache,
      refresh,
      organisms: data.organisms,
      antibiotics: data.antibiotics,
      wards: data.wards,
      specimens: data.specimens,
      organismName: (code) => (code ? organismsByCode.get(code)?.name ?? code : "—"),
      wardName: (code) => (code ? wardsByCode.get(code)?.name ?? code : "—"),
      specimenName: (code) => (code ? specimensByCode.get(code)?.name ?? code : "—"),
    };
  }, [bundle, loading, fromCache, refresh]);

  return (
    <ReferenceContext.Provider value={value}>{children}</ReferenceContext.Provider>
  );
}

export function useReference(): ReferenceValue {
  const context = useContext(ReferenceContext);
  if (!context) throw new Error("useReference must be used inside a ReferenceProvider");
  return context;
}
